import React, { FC, useEffect } from "react";

// UI
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { Container } from "@UI/Components/Container/Container";
import { Box } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BrandPlus } from "@UI/Components/Icons/BrandPlus";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// Core
import { SelectSubscriptionPlan } from "@modules/Subscriptions/Components/SelectSubscriptionPlan/SelectSubscriptionPlan";
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { ISubscriptionPlan } from "@modules/Subscriptions/types";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { isSubscriptionProduct } from "@modules/Subscriptions/services/subscriptionPlans";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { IErrorResponse } from "@api/createApi";
import { useDataLayer } from "@Libs/DataLayer/hooks/useDataLayer";
import { v4 as uuidv4 } from "uuid";
import { IUnsavedBasket } from "@modules/Purchasing/services/baskets";
import { useSubscribeFlow } from "@modules/Subscriptions/hooks/useSubscribeFlow";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";

export const ChooseSubscriptionPlan: FC = () => {
  const { isMobile } = useMediaQuery();
  const dataLayer = useDataLayer();
  const navigate = useNavigate();
  const { backRoute, onLeave, onPageMount, navigateToStep } =
    useSubscribeFlow();
  const {
    saveBasket,
    isLoading,
    currentBasket,
    currentBasketId,
    isError,
    isSavingBasket,
  } = useCurrentBasket();
  const paramsBasketId = useSearchParams("resourceId");

  const isBasketFromParamsLoading = !!(
    paramsBasketId && currentBasketId !== paramsBasketId
  );

  const handleLockedBasket = ({ items }) => {
    dataLayer.setQueryData("currentBasketId", null);
    const id = uuidv4();
    saveBasket(
      {
        id,
        items,
      },
      {
        onSuccess: (basket) => {
          dataLayer.setQueryData("currentBasketId", id);
          dataLayer.setQueryData("currentBasket", basket);
        },
      }
    );
  };

  const handleSelect = (paymentPlan: ISubscriptionPlan) => {
    const nonSubscriptionItems = currentBasket.items.filter(
      (basketItem) => basketItem.itemType !== "product"
    );

    const items: IUnsavedBasket["items"] = [
      ...nonSubscriptionItems.map(({ itemId, itemType, quantity }) => ({
        itemId,
        itemType,
        quantity,
      })),
      {
        itemId: paymentPlan.id,
        itemType: "product",
        quantity: 1,
      },
    ];
    saveBasket(
      {
        id: currentBasket.id,
        items,
      },
      {
        onSuccess: () => {
          navigateToStep(Checkout.root);
        },
        onError: async (error: IErrorResponse) => {
          if (error.response?.status === 423) {
            handleLockedBasket({ items });
            navigateToStep(Checkout.root);
          } else {
            navigate(["SERVER_ERROR"]);
          }
        },
      }
    );
  };

  useEffect(() => {
    if (
      ((!isLoading && !paramsBasketId && !isError) ||
        (paramsBasketId && !isBasketFromParamsLoading)) &&
      currentBasket.items.find(({ item }) => isSubscriptionProduct(item))
    ) {
      const nonSubscriptionItems = currentBasket.items.filter(
        (basketItem) => basketItem.itemType !== "product"
      );
      saveBasket(
        {
          id: currentBasket.id,
          items: nonSubscriptionItems,
        },
        {
          onError: (error: IErrorResponse) => {
            if (error.response?.status === 423) {
              handleLockedBasket({ items: nonSubscriptionItems });
            }
          },
        }
      );
    }
  }, []);

  const handleBackClick = () => {
    onLeave ? onLeave() : navigate(backRoute.to, backRoute);
  };

  useEffect(() => {
    if (isLoading || isSavingBasket) return;
    onPageMount?.("CHOOSE_PLAN");
  }, [isLoading]);

  if (isLoading || isSavingBasket || isBasketFromParamsLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Wizard>
        <WizardNav>
          <BackLink onClick={handleBackClick} />
        </WizardNav>
        <WizardHeader>
          <Heading>
            <FormattedMessage
              {...messages.chooseYourSubscriptionPaymentPlan}
              values={{
                plus: () => (
                  <Icon
                    as={BrandPlus}
                    boxSize="5"
                    marginLeft="1"
                    display="inline"
                  />
                ),
              }}
            />
          </Heading>
        </WizardHeader>
        <WizardContent>
          <Box marginBottom={{ base: 4, lg: 10 }}>
            <SelectSubscriptionPlan onSelect={handleSelect} />
          </Box>
          <Box textAlign="center">
            <Text fontSize={{ base: "sm", md: "md" }}>
              <FormattedMessage
                {...messages.youCanCancelTheRenewalOfYourSubscriptionAtAnyMoment}
              />
            </Text>
          </Box>
          {isMobile && (
            <Box marginTop="10">
              <FAQSection />
            </Box>
          )}
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};
